import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type JSX, lazy, type ReactNode, Suspense } from 'react';
import { BrowserRouter, NavLink, Route, Routes, useLocation } from 'react-router';
import { TALLINN } from '../../src/wire';
import { ApiError } from './api';
import { BacklogChip } from './components/BacklogChip';
import { ChronicleButton } from './components/ChronicleButton';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PendingNote } from './components/PendingNote';
import { ShieldMark } from './components/ShieldMark';
import { SiteFooter } from './components/SiteFooter';
import { SiteNav } from './components/SiteNav';
import Landing from './pages/Landing';
import { useMeta } from './useStats';

/**
 * The application shell: providers, the shared header, and the route table (spec §7). Every route
 * is a real, hand-built page — there is no shared template and no taxonomy-driven dashboard, which
 * is why `/vargused` leads with a euro total and `/liiklusonnetused` shows no money at all. All
 * user-facing text is hardcoded Estonian; there is no i18n layer and none is planned.
 */

/**
 * One client for the process.
 *
 * `staleTime` is five minutes because the corpus behind these queries changes once a day at most —
 * the scrape is a cron job (spec §5) — so a refetch on every remount would be pure traffic.
 *
 * **A refused request is never retried.** The API is same-origin and single-process, and it answers
 * a bad window or a bad cursor with a field-level 4xx (`routes.ts`) — a verdict on the request
 * itself, which the next identical request will earn again. Retrying it only delays the message the
 * reader needs by a round trip while pretending something is loading. Anything else — a dropped
 * connection, a 500 while the box is busy — gets exactly one second chance, not the default three.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/** Every page that draws a chart is loaded on demand; `Landing` stays eager because it is the
 * entry point, and a second round trip there would trade the fast case for the slow one. */
const TheftPage = lazy(() => import('./pages/TheftPage'));
const FraudPage = lazy(() => import('./pages/FraudPage'));
const TrafficPage = lazy(() => import('./pages/TrafficPage'));
const DomesticViolencePage = lazy(() => import('./pages/DomesticViolencePage'));
const GenericCategoryPage = lazy(() => import('./pages/GenericCategoryPage'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));

// ─── Header ─────────────────────────────────────────────────────────────────

/**
 * `runs.finished_at` is an ISO timestamp; anything else is shown verbatim rather than as `NaN`.
 *
 * Rendered in **Europe/Tallinn**, not in the viewer's zone. Every other date on this site is a
 * Tallinn calendar day — the corpus is Estonian police reports and `queries.ts` buckets by Tallinn
 * days — so a freshness line in the reader's own zone would sit above charts keyed to a different
 * one. For a reader abroad that is not a cosmetic difference: "updated 12 August 23:00" next to a
 * chart whose last bar is 13 August reads as data arriving before it was collected.
 */
const TIMESTAMP = new Intl.DateTimeFormat('et-EE', {
  dateStyle: 'long',
  timeStyle: 'short',
  timeZone: TALLINN,
});

function formatTimestamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return TIMESTAMP.format(at);
}

/**
 * Title, freshness and navigation — the three things visible on every page.
 *
 * The freshness line is not decoration. This dashboard reports crime, and a reader who cannot tell
 * whether a quiet week is quiet or simply unscraped has been misled by the chart. `last_scrape_at`
 * only ever names a *finished* run (`queries.ts`), so it can never claim data that did not land.
 *
 * Exported for the render tests: `App` itself mounts a `BrowserRouter` and cannot be server
 * rendered, and the backlog claim it carries is an honesty claim like any other on the site.
 */
export function Header(): JSX.Element {
  const { data: meta, isError } = useMeta();

  return (
    <header className="border-b-[3px] border-accent bg-[image:var(--masthead-band)]">
      <div className="mx-auto w-full max-w-[76rem] px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 pt-4">
          <NavLink to="/" className="flex items-center gap-2.5 text-ink no-underline">
            <ShieldMark />
            <span className="font-display text-[1.45rem] font-bold tracking-[-0.015em]">
              Politseikroonika
            </span>
          </NavLink>

          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
            <p className="m-0 text-small text-ink-muted">
              {isError
                ? 'Andmete seisu ei õnnestunud laadida.'
                : meta === undefined
                  ? 'Laadin andmete seisu…'
                  : meta.last_scrape_at === null
                    ? 'Andmeid pole veel kogutud.'
                    : `Viimati uuendatud: ${formatTimestamp(meta.last_scrape_at)}`}
            </p>
            {meta !== undefined && (
              <BacklogChip pending={meta.pending_extractions} coverage={meta.coverage} />
            )}
            <ChronicleButton />
          </div>
        </div>

        <SiteNav />
      </div>
    </header>
  );
}

// ─── Not found ──────────────────────────────────────────────────────────────

/**
 * A URL this app has no route for.
 *
 * A *category* that does not exist is a different mistake with a better answer, and
 * `GenericCategoryPage` handles it — this one only sees paths outside the route table entirely.
 */
function NotFoundPage(): JSX.Element {
  return (
    <section className="page">
      <h1>Lehte ei leitud</h1>
      <p className="muted">
        Sellist lehte siin ei ole. <NavLink to="/">Tagasi avalehele</NavLink>.
      </p>
    </section>
  );
}

// ─── The app ────────────────────────────────────────────────────────────────

/** The routed half's boundary, reset by navigation: a caught throw belongs to the page it happened
 * on, and every link in the masthead would otherwise lead back to the same note. */
export function PageBoundary({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ErrorBoundary what="Lehe" resetKey={useLocation().pathname}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * The layout every route renders inside, with a boundary around each half: a masthead that throws
 * must not take the page with it, and a page that throws must not take the masthead.
 *
 * Exported for the render tests, which cannot mount `App` — it carries a `BrowserRouter`.
 */
export function Shell(): JSX.Element {
  return (
    <div className="flex min-h-dvh flex-col bg-paper text-ink">
      <ErrorBoundary what="Päise">
        <Header />
      </ErrorBoundary>
      <main className="mx-auto w-full max-w-[76rem] flex-1 px-4 py-6 sm:px-6">
        <PageBoundary>
          {/* One fallback for every lazy route: the page's own chunk is a single request against
              the same origin that served this one, so what is being waited for is milliseconds of
              network, not a spinner's worth of work. The word is the one every panel on the site
              uses while it waits. */}
          <Suspense fallback={<PendingNote />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/vargused" element={<TheftPage />} />
              <Route path="/kelmused" element={<FraudPage />} />
              <Route path="/liiklusonnetused" element={<TrafficPage />} />
              <Route path="/lahisuhtevagivald" element={<DomesticViolencePage />} />
              <Route path="/metoodika" element={<MethodologyPage />} />
              <Route path="/kategooria/:category" element={<GenericCategoryPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </PageBoundary>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
